import { z } from "zod";

// Phase 11 typed configSchema for the `option-tier-matrix` template.
// Pure schema source — Plan 02 swaps it into TEMPLATE_CATALOG; this file ships no
// renderer. validateRecipe Pass 2A runs configSchema.safeParse(widget.config) so
// this schema is the config-shape gate a recipe's option-tier-matrix widget crosses.
//
// Shape rationale (closest analog: ranked-tier.ts). ranked-tier is a FLAT per-option
// single-tier ranking (one tier id per option). option-tier-matrix is a per-option
// N-row matrix: each option owns its own ordered rows, and each row carries an
// ordered list of cells. The user picks a row at render time (that selection state
// is render-side and is NOT modeled here). Composition is by id — `optionSetWidgetId`
// references an option-set widget instance in the same recipe (mirrors ranked-tier).
//
// Generality (the C1 grep gate): every field name and every comment here is generic —
// options / rows / cells / rowColors. Recipe-declared ids and cell strings carry any
// domain meaning; the engine never names a recipe's vocabulary.
//
// rowColors is an OPTIONAL bounded list of `{ bg, border, fg }` color trios reusing
// the same CSS-color positive-list as ranked-tier (rejects expression(, url(,
// javascript:, <, >, ;). A local copy of the regex + trio shape keeps this file
// self-contained rather than coupling to ranked-tier across modules.
//
// DoS bounds (T-11-01-01): options <= 50, rows <= 12, cells <= 4, rowColors <= 12;
// every string is length-bounded. Bounds an unbounded extracted config before it
// reaches storage. The cells cap (4) keeps each row a small fixed-width tuple.
//
// Cross-widget existence-check (does optionSetWidgetId point to an existing
// option-set? do option ids line up with that set?) is NOT enforced here — that
// requires a recipe-root walk (future Pass 2B).
//
// isolatedDeclarations + verbatimModuleSyntax + exactOptionalPropertyTypes (D-15):
// every exported type is an explicit interface and the exported const carries a
// `z.ZodType<OptionTierMatrixConfig>` annotation so tsc emits declarations without
// reading across module boundaries (no TS9013).

// CSS color format positive-list — admits hex, rgb/rgba, hsl/hsla, oklch, oklab,
// lab, lch, color(), and a named-color identifier; rejects the canonical
// CSS-injection delimiters (`<`, `>`, `;`) inside function args and the
// expression(/url(/javascript:/attr(/var( smuggling vectors (they match no allowed
// alternative). Copied from ranked-tier.ts:94-95 — see that file's header for the
// full per-alternative rationale.
const CSS_COLOR_RE =
  /^(#[0-9a-fA-F]{3,8}|rgba?\([^<>;]*\)|hsla?\([^<>;]*\)|oklch\([^<>;]*\)|oklab\([^<>;]*\)|lab\([^<>;]*\)|lch\([^<>;]*\)|color\([^<>;]*\)|[a-zA-Z]+)$/;

export interface OptionTierMatrixColorTrio {
  readonly bg: string;
  readonly border: string;
  readonly fg: string;
}

export interface OptionTierMatrixRow {
  id: string;
  label: string;
  cells: ReadonlyArray<string>;
}

export interface OptionTierMatrixOption {
  id: string;
  rows: ReadonlyArray<OptionTierMatrixRow>;
}

export interface OptionTierMatrixConfig {
  optionSetWidgetId: string;
  options: ReadonlyArray<OptionTierMatrixOption>;
  rowColors?: ReadonlyArray<OptionTierMatrixColorTrio> | undefined;
}

// Color trio strictObject — each component a non-empty length-bounded string
// passing the CSS_COLOR_RE positive list (defense-in-depth at the input-validator
// layer; the browser CSS parser is the second layer).
const _tierColorsSchema = z.strictObject({
  bg: z.string().min(1).max(50).regex(CSS_COLOR_RE, "CSS color value required"),
  border: z.string().min(1).max(50).regex(CSS_COLOR_RE, "CSS color value required"),
  fg: z.string().min(1).max(50).regex(CSS_COLOR_RE, "CSS color value required"),
});

// Per-row schema — kebab-case id + label + an ordered tuple of cells. cells.min(1):
// a row with zero cells is meaningless; cells.max(4): each row is a small
// fixed-width tuple (DoS bound). Each cell is a non-empty length-bounded string.
const _rowSchema = z.strictObject({
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  label: z.string().min(1).max(200),
  cells: z.array(z.string().min(1).max(200)).min(1).max(4),
});

// Per-option schema — a kebab-case id + a bounded ordered list of rows. rows.min(1):
// an option with zero rows is meaningless; rows.max(12): mirrors ranked-tier's
// tier cap (past ~12 the matrix is unreadable).
const _optionSchema = z.strictObject({
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  rows: z.array(_rowSchema).min(1).max(12),
});

// Top-level schema: optionSetWidgetId is the composition ref; options.min(1).max(50)
// (a matrix needs at least one option; 50 is a generous soft cap). rowColors is
// optional and capped at 12. The superRefine emits per-index custom issues for
// duplicate option ids and, per option, duplicate row ids — mirroring ranked-tier's
// per-index loop (one ctx.addIssue per offence).
export const OptionTierMatrixConfigSchema: z.ZodType<OptionTierMatrixConfig> = z
  .strictObject({
    optionSetWidgetId: z.string().min(1).max(100),
    options: z.array(_optionSchema).min(1).max(50),
    rowColors: z.array(_tierColorsSchema).max(12).optional(),
  })
  .superRefine((cfg, ctx) => {
    const seenOptionIds = new Set<string>();
    for (const [i, option] of cfg.options.entries()) {
      if (seenOptionIds.has(option.id)) {
        ctx.addIssue({
          code: "custom",
          path: ["options", i, "id"],
          message: `duplicate option id "${option.id}"`,
        });
      }
      seenOptionIds.add(option.id);
      const seenRowIds = new Set<string>();
      for (const [j, row] of option.rows.entries()) {
        if (seenRowIds.has(row.id)) {
          ctx.addIssue({
            code: "custom",
            path: ["options", i, "rows", j, "id"],
            message: `duplicate row id "${row.id}" in option "${option.id}"`,
          });
        }
        seenRowIds.add(row.id);
      }
    }
  });
